import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { GridApi, GridOptions } from 'ag-grid-community';
import deepcopy from 'deepcopy';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ContactStateCellRendererComponent } from '../contact-list-details/contact-state-cell-renderer/contact-state-cell-renderer.component';
import { ContactListZoomDetailsObject, DetailedStateObj } from '../contact-list.models';

@Component({
    selector: 'cv-contact-list-zoom',
    standalone: true,
    imports: [CommonModule, AgGridModule],
    templateUrl: './contact-list-zoom.component.html',
    styleUrl: './contact-list-zoom.component.scss'
})

export class ContactListZoomComponent {
    @ViewChild('contactListZoomGrid') agGrid: AgGridAngular;
    @Input() data: DetailedStateObj;
    @Input() contactNo: string;

    public gridOptions: GridOptions;
    public localizationDict = new Map<string, string>();
    public contactNoLocalized = "Contact No";
    public totalLocalized = "Total";
    private gridApi: GridApi;
    private rowData = [];

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private activeModal: NgbActiveModal,
    ) {}

    async ngOnInit(): Promise<void>{
        this.localizationDict = await this.cvLocalizationService.GetSection('Contact_List');
        this.contactNoLocalized = this.cvLocalizationService.getCachedText('Contact_List', 'Contact_No', 'Contact No');
        this.totalLocalized = this.cvLocalizationService.getCachedText('Contact_List', 'Total', 'Total');

        const details = this.data?.Details as ContactListZoomDetailsObject[];
        this.rowData = details?.map(detail => ({
            State: detail.StateName,
            Date: detail.StartDate,
            DateDisplay: detail.StartDateDisplay,
            Time: detail.StartTimeDisplay,
            Duration: detail.TotalTimeDisplay,
            Name: detail.AgentName,
            Skill: detail.SkillName,
            ContactID: detail.ContactID,
        }));

        this.createGrid();
    }

    createGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            components: {
                ContactStateCellRenderer : ContactStateCellRendererComponent,
            },
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            rowHeight: 40,
            headerHeight: 44,
            rowClass: 'cx-one-ui',
            suppressContextMenu: true,
            getMainMenuItems: (params: any) => {
                const menuItems = deepcopy(params.defaultItems);
                return menuItems;
            },
            onGridReady: params => {
                this.gridApi = params.api;
                if (this.rowData) {
                    this.gridApi.setGridOption('rowData', this.rowData);
                }
                this.agGridHelperService.sizeColumnsFromApisSmart(this.gridApi);
            },
            onGridSizeChanged: event => {
                this.agGridHelperService.sizeColumnsSmart(event);
            },
            columnDefs: [
                {
                    field: 'State',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Contact_State','Contact State'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'ContactStateCellRenderer',
                    cellRendererParams: params => ({ showTotals: false, data: this.data?.Details?.[params.rowIndex] }),
                    resizable: true,
                    suppressMovable: true,
                    suppressMenu: true
                },
                {
                    field: 'DateDisplay',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Contact_Date','Contact Date'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: params => "<div class='d-flex flex-column justify-content-center h-100'>" + params.value + "</div>",
                    resizable: true,
                    suppressMovable: true,
                    suppressMenu: true,
                    comparator: (valueA, valueB, nodeA, nodeB) =>
                        this.agGridHelperService.dateComparator(new Date(nodeA.data.Date), new Date(nodeB.data.Date)),
                },
                {
                    field: 'Time',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Start_Time','Start Time'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: params => "<div class='d-flex flex-column justify-content-center h-100'>" + params.value + "</div>",
                    resizable: true,
                    suppressMovable: true,
                    suppressMenu: true,
                    comparator: (valueA, valueB, nodeA, nodeB) =>
                        this.agGridHelperService.dateComparator(new Date(nodeA.data.Date), new Date(nodeB.data.Date)),
                },
                {
                    field: 'Duration',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Duration', 'Duration'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: params => "<div class='d-flex flex-column justify-content-center h-100'>" + params.value + "</div>",
                    resizable: true,
                    suppressMovable: true,
                    suppressMenu: true
                },
                {
                    field: 'Name',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Agent_Name', 'Agent Name'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: params => "<div class='d-flex flex-column justify-content-center h-100'>" + params.value + "</div>",
                    resizable: true,
                    suppressMovable: true,
                    suppressMenu: true
                },
                {
                    field: 'Skill',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Skill_Name', 'Skill Name'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: params => "<div class='d-flex flex-column justify-content-center h-100'>" + params.value + "</div>",
                    resizable: true,
                    suppressMovable: true,
                    suppressMenu: true
                },
            ],
            rowData: this.rowData,
        },
        {
            enableCopy: true
        });
    }

    close() {
        this.activeModal.close();
    };
}
